import { getHelper, type HelperDefinition } from './helperRegistry'

export const pathToLabel = (path: string): string => {
    return path
        .split('.')
        .map(segment =>
            segment
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/_/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase()),
        )
        .join(' \u2192 ')
}

export const isVariablePath = (path: string): boolean => {
    return /^(user|journey)\./.test(path)
}

export interface HelperArg {
    value: string
    quoted: boolean
}

export const isHelperExpression = (text: string): boolean => {
    const name = text.trim().split(/\s+/)[0]
    return !!getHelper(name)
}

export const parseHelperExpression = (text: string): { name: string, args: HelperArg[], definition: HelperDefinition } | null => {
    const trimmed = text.trim()
    const tokens = tokenizeHelper(trimmed)
    if (tokens.length === 0) return null

    const name = tokens[0]
    const definition = getHelper(name)
    if (!definition) return null

    const args: HelperArg[] = tokens.slice(1).map(token => {
        if ((token.startsWith('"') && token.endsWith('"'))
            || (token.startsWith("'") && token.endsWith("'"))) {
            return { value: token.slice(1, -1), quoted: true }
        }
        return { value: token, quoted: false }
    })

    return { name, args, definition }
}

const tokenizeHelper = (text: string): string[] => {
    const tokens: string[] = []
    let current = ''
    let inQuote: string | null = null

    for (let i = 0; i < text.length; i++) {
        const char = text[i]

        if (inQuote) {
            current += char
            if (char === inQuote) {
                tokens.push(current)
                current = ''
                inQuote = null
            }
        } else if (char === '"' || char === "'") {
            inQuote = char
            current = char
        } else if (char === ' ' || char === '\t') {
            if (current) {
                tokens.push(current)
                current = ''
            }
        } else {
            current += char
        }
    }

    if (current) {
        tokens.push(current)
    }

    return tokens
}

export const serializeHelperArgs = (helperName: string, args: HelperArg[]): string => {
    const parts = [helperName]
    for (const arg of args) {
        if (arg.quoted) {
            parts.push(`"${arg.value}"`)
        } else {
            parts.push(arg.value)
        }
    }
    return parts.join(' ')
}

export const helperPillLabel = (helperName: string, args: HelperArg[]): string => {
    const definition = getHelper(helperName)
    const label = definition?.label ?? helperName

    const firstVarArg = args.find(a => !a.quoted && a.value)
    if (firstVarArg) {
        return `${label}: ${pathToLabel(firstVarArg.value)}`
    }
    return label
}
